import { useEffect, useMemo, useState } from 'react';
import CustomBusinessRulesService from 'components/business-rules-custom/CustomBusinessRulseServices';

/**
 * Fetch business rules for either organisation or channel.
 *
 * @param {Object} params
 * @param {'organisation'|'channel'} params.scope - Scope of rules
 * @param {string} [params.organisationId] - Organisation ID (required if scope is 'organisation')
 * @param {string} [params.channelId] - Channel ID (required if scope is 'channel')
 *
 * @returns {Promise<Array<{name: string, value: any}>>} Array of business rules
 */
const fetchBusinessRules = async (params) => {
  try {
    switch (params.scope) {
      case 'organisation':
        if (!params.organisationId) {
          throw new Error('organisationId is required for organisation rules');
        }
        return (
          await CustomBusinessRulesService.getCustomBusinessRulesOrganisation(
            params.organisationId,
          )
        ).data;

      case 'channel':
        if (!params.channelId) {
          throw new Error('channelId is required for channel rules');
        }
        return (
          await CustomBusinessRulesService.getAllBusinessRulesChannel(
            params.channelId,
          )
        ).data;

      default:
        throw new Error(`Unknown scope: ${params.scope}`);
    }
  } catch (error) {
    console.error('Failed to fetch business rules', error);
    throw error;
  }
};

/**
 * Custom hook to fetch and manage business rules for organisation or channel.
 *
 * This is the way to read a rule value. Channel rules come from GetAllBusinessRulesChannel.
 * GetBusinessRulesChannel is only for the Channel Profiles editor, do not use it to read values.
 *
 * @param {Object} params
 * @param {'organisation'|'channel'} params.scope - Scope of rules
 * @param {string} [params.organisationId] - Organisation ID (required if scope is 'organisation')
 * @param {string} [params.channelId] - Channel ID (required if scope is 'channel')
 *
 * @returns {Object} API:
 *   - rules: Array<{name, value}>
 *   - hasRule(name: string): boolean — true if the rule exists and has a value
 *   - hasAllRules(names: string[]): boolean — true if all rules exist and have values
 *   - getRuleValue(name: string): any — value of a specific rule
 *   - loading: boolean — true while fetching business rules
 *   - error: any — error object if fetch failed
 *
 * @example
 * - Organisation rules
 * const { hasRule, hasAllRules, getRuleValue, getAllRuleValues, loading, error } = useBusinessRule({
 *   scope: 'organisation',
 *   organisationId: user.organisationId
 * });
 *
 * if (loading) return <p>Loading organisation rules...</p>;
 * if (error) return <p>Error loading rules</p>;
 *
 * const hasMinutage = hasRule('Commercial Minutage');
 * const allRulesPresent = hasAllRules(['Commercial Minutage', 'Commercial dayparts']);
 * const minutageLimit = getRuleValue('Commercial Minutage');
 *
 * - Channel rules
 * const { hasRule: hasChannelRule, getAllRuleValues: getChannelRules } = useBusinessRule({
 *   scope: 'channel',
 *   channelId: someChannelId
 * });
 * const hasPrimeTime = hasChannelRule('Prime Time Access');
 * const primeTimeRuleValue = getRuleValue('Prime Time Access');
 */
export const useBusinessRule = (params) => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Skip fetch if required parameters are missing
    if (
      (params.scope === 'organisation' && !params.organisationId) ||
      (params.scope === 'channel' && !params.channelId)
    ) {
      setRules([]);
      setLoading(false);
      return;
    }

    const loadRules = async () => {
      setLoading(true);
      try {
        const data = await fetchBusinessRules(params);
        setRules(Array.isArray(data) ? data : []); // Normalize: ensure rules is always an array
      } catch (err) {
        console.error(err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    loadRules();
  }, [params.scope, params.organisationId, params.channelId]);

  const api = useMemo(
    () => ({
      rules,

      /**
       * Check if a specific rule exists and has a value
       * @param {string} name
       * @returns {boolean}
       */
      hasRule: (name) =>
        rules.some((r) => r.name === name && r.value != null && r.value !== ''),

      /**
       * Check if all given rules exist and have values
       * @param {string[]} names
       * @returns {boolean}
       */
      hasAllRules: (names) =>
        names.every((name) =>
          rules.some(
            (r) => r.name === name && r.value != null && r.value !== '',
          ),
        ),

      /**
       * Get the value of a specific rule
       * @param {string} name
       * @returns {any}
       */
      getRuleValue: (name) =>
        rules?.find((r) => r.name === name)?.value ?? null,
    }),
    [rules],
  );

  return {
    ...api,
    loading,
    error,
  };
};
